import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

/** Size classes from DESIGN-RULES 2: phone < 768 <= tablet < 1180 <= laptop < 1366 <= desktop. */
export type SizeClass = "phone" | "tablet" | "laptop" | "desktop";
const QUERIES: [SizeClass, string][] = [
  ["phone", "(width < 768px)"],
  ["tablet", "(768px <= width < 1180px)"],
  ["laptop", "(1180px <= width < 1366px)"],
  ["desktop", "(width >= 1366px)"],
];
function readSize(): SizeClass {
  if (typeof window === "undefined") return "desktop";
  return QUERIES.find(([, q]) => window.matchMedia(q).matches)?.[0] ?? "desktop";
}
function subscribeSize(cb: () => void) {
  const lists = QUERIES.map(([, q]) => window.matchMedia(q));
  for (const l of lists) l.addEventListener("change", cb);
  return () => { for (const l of lists) l.removeEventListener("change", cb); };
}
export const useSize = () => useSyncExternalStore(subscribeSize, readSize, () => "desktop" as SizeClass);

/** A media query as a boolean. */
export function useMedia(query: string) {
  return useSyncExternalStore(
    (cb) => { const l = window.matchMedia(query); l.addEventListener("change", cb); return () => l.removeEventListener("change", cb); },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** Remember a region's scroll position per key (DESIGN-RULES 3), across panel toggles and back navigation. */
const scrollStore = new Map<string, { top: number; left: number }>();
export function useScrollMemory<T extends HTMLElement>(key: string | undefined) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !key) return;
    const saved = scrollStore.get(key);
    if (saved) { el.scrollTop = saved.top; el.scrollLeft = saved.left; }
    const onScroll = () => scrollStore.set(key, { top: el.scrollTop, left: el.scrollLeft });
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [key]);
  return ref;
}

/** True after `ms`, so skeletons appear only for slow loads (DESIGN-RULES 9: after 150 ms). */
export function useDelayed(active: boolean, ms = 150) {
  const subscribers = useRef(new Set<() => void>());
  const shown = useRef(false);
  useEffect(() => {
    shown.current = false;
    if (!active) return;
    const t = setTimeout(() => { shown.current = true; for (const s of subscribers.current) s(); }, ms);
    return () => clearTimeout(t);
  }, [active, ms]);
  const subscribe = useCallback((cb: () => void) => { subscribers.current.add(cb); return () => subscribers.current.delete(cb); }, []);
  return useSyncExternalStore(subscribe, () => active && shown.current, () => false);
}

/** Width of an element, live (container-driven layouts: columns drop as the region narrows, not the window). */
export function useElementWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const width = useRef(0);
  const subs = useRef(new Set<() => void>());
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry!.contentRect.width);
      if (w !== width.current) { width.current = w; for (const s of subs.current) s(); }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const subscribe = useCallback((cb: () => void) => { subs.current.add(cb); return () => subs.current.delete(cb); }, []);
  const w = useSyncExternalStore(subscribe, () => width.current, () => 0);
  return [ref, w] as const;
}

/** True when the person asked for reduced motion (DESIGN-RULES 10). */
export function usePrefersReducedMotion() {
  return useMedia("(prefers-reduced-motion: reduce)");
}
